/**
 * CASL Ability Definitions (Frontend)
 * Defines permissions based on user role and type within a clinic context
 */

import { AbilityBuilder, createMongoAbility } from '@casl/ability';

/**
 * Define abilities for a user in a specific clinic context
 * @param {Object} user - User object with memberships
 * @param {string} currentClinicId - Current clinic ID
 * @returns {MongoAbility} CASL ability instance
 */
export function defineAbilitiesFor(user, currentClinicId) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);

  if (!user || !currentClinicId) {
    // No permissions if no user or clinic context
    return build();
  }

  // Find user's membership in the current clinic
  const membership = user.memberships?.find((m) => m.clinicId === currentClinicId || m.clinic?.id === currentClinicId);

  if (!membership) {
    // No permissions if not a member
    return build();
  }

  const role = membership.role;
  const isOwnerOrAdmin = ['OWNER', 'ADMIN'].includes(role);
  const isDoctor = user.type === 'DOCTOR';

  // ===========================================================================
  // Consent Permissions
  // ===========================================================================

  // Everyone can view consents in their clinic
  can('view', 'Consent', { clinicId: currentClinicId });

  // Everyone can create consents
  can('create', 'Consent', { clinicId: currentClinicId });

  // Everyone can update pending consents
  can('update', 'Consent', {
    clinicId: currentClinicId,
    status: { $in: ['PENDING_DOCTOR_SIGNATURE', 'PENDING_PATIENT_SIGNATURE'] },
  });

  // Only doctors can sign consents (their own)
  if (isDoctor) {
    can('sign', 'Consent', {
      clinicId: currentClinicId,
      doctorId: user.id,
      status: 'PENDING_DOCTOR_SIGNATURE',
    });
  }

  // Only owners and admins can cancel/void consents
  if (isOwnerOrAdmin) {
    can('cancel', 'Consent', { clinicId: currentClinicId });
  }

  // ===========================================================================
  // Procedure Template Permissions
  // ===========================================================================

  // Everyone can view templates
  can('view', 'ProcedureTemplate', { clinicId: currentClinicId });

  // OWNER, ADMIN, DOCTOR can create/update/delete templates
  // (only STAFF cannot)
  if (role !== 'STAFF') {
    can('create', 'ProcedureTemplate', { clinicId: currentClinicId });
    can('update', 'ProcedureTemplate', { clinicId: currentClinicId });
    can('delete', 'ProcedureTemplate', { clinicId: currentClinicId });
  }

  // ===========================================================================
  // Member Management Permissions
  // ===========================================================================

  // Everyone can view members
  can('view', 'Member', { clinicId: currentClinicId });

  // OWNER and ADMIN can invite users
  if (isOwnerOrAdmin) {
    can('invite_user', 'Clinic', { id: currentClinicId });
    can('view', 'Invite', { clinicId: currentClinicId });
    can('delete', 'Invite', { clinicId: currentClinicId });
  }

  // OWNER and ADMIN can manage members
  if (isOwnerOrAdmin) {
    can('remove', 'Member', { clinicId: currentClinicId });
    can('change_role', 'Member', { clinicId: currentClinicId });
  }

  // ADMIN cannot modify OWNER roles
  if (role === 'ADMIN') {
    cannot('remove', 'Member', { role: 'OWNER' });
    cannot('change_role', 'Member', { role: 'OWNER' });
  }

  // ===========================================================================
  // Clinic Permissions
  // ===========================================================================

  // Everyone can view their clinic
  can('view', 'Clinic', { id: currentClinicId });

  // OWNER and ADMIN can update clinic settings
  if (isOwnerOrAdmin) {
    can('update', 'Clinic', { id: currentClinicId });
  }

  // Only OWNER can delete clinic
  if (role === 'OWNER') {
    can('delete', 'Clinic', { id: currentClinicId });
  }

  return build();
}

/**
 * Check if user can perform an action on a subject
 * Helper function for easier permission checking
 */
export function checkPermission(user, currentClinicId, action, subject, field) {
  const ability = defineAbilitiesFor(user, currentClinicId);
  return ability.can(action, subject, field);
}

export default defineAbilitiesFor;
